import { useEffect, useRef, useState } from 'react';
import type { MapLocation } from '@repo/shared';
import type { SavedLocation } from '../lib/saved-locations';
import styles from './SavedLocations.module.css';

export interface SavedLocationsProps {
  locations: readonly SavedLocation[];
  /** The place currently shown, offered for saving. Null before anything is picked. */
  current: MapLocation | null;
  isCurrentSaved: boolean;
  onSelect: (location: SavedLocation) => void;
  onSave: (location: MapLocation) => void;
  onRemove: (id: string) => void;
}

/** Dropdown of places kept in this browser, plus a save action for the current one. */
export function SavedLocations({
  locations,
  current,
  isCurrentSaved,
  onSelect,
  onSave,
  onRemove,
}: SavedLocationsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const canSave = current !== null && !isCurrentSaved;

  return (
    <div className={styles.wrap} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <StarIcon filled={isCurrentSaved} />
        <span className={styles.label}>Places</span>
        {locations.length > 0 && <span className={styles.count}>{locations.length}</span>}
      </button>

      {isOpen && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.saveCurrent}
            disabled={!canSave}
            onClick={() => {
              if (current) onSave(current);
            }}
          >
            {current === null
              ? 'Pick a place to save it'
              : isCurrentSaved
                ? `“${current.label}” is saved`
                : `Save “${current.label}”`}
          </button>

          {locations.length === 0 ? (
            <p className={styles.empty}>
              Saved places live in this browser only — nothing is sent to a server.
            </p>
          ) : (
            <ul className={styles.list}>
              {locations.map((location) => (
                <li key={location.id} className={styles.item}>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.itemButton}
                    onClick={() => {
                      onSelect(location);
                      setIsOpen(false);
                    }}
                  >
                    {location.label}
                  </button>
                  <button
                    type="button"
                    className={styles.remove}
                    onClick={() => onRemove(location.id)}
                    aria-label={`Remove ${location.label}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 1.8 10 6l4.5.5-3.4 3 1 4.4L8 11.7 3.9 13.9l1-4.4-3.4-3L6 6z" />
    </svg>
  );
}
